import React from 'react';
import { Button, Fab, IconButton, Tooltip } from '@mui/material';
import {
  ContactSupport as ContactSupportIcon,
  HeadsetMic as HeadsetMicIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSupportChat } from '../../context/SupportChatContext';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const iconVariantSx = {
  default: {
    color: BRAND.text,
    bgcolor: brandAlpha('accent', 0.1),
    '&:hover': { bgcolor: brandAlpha('accent', 0.2) },
  },
  plain: {
    color: BRAND.accentText,
  },
};

const contactLabelKey = (isStoreChat) => (
  isStoreChat ? 'common.contactStoreChat' : 'common.contactSupport'
);

export const ContactSupportIconButton = ({ variant = 'default', sx }) => {
  const { t } = useTranslation();
  const { openChat, isAppAdmin, isStoreChat } = useSupportChat();
  const label = t(contactLabelKey(isStoreChat));

  if (isAppAdmin) {
    return null;
  }

  return (
    <Tooltip title={label}>
      <IconButton
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openChat();
        }}
        aria-label={label}
        sx={{ ...iconVariantSx[variant], ...sx }}
      >
        <ContactSupportIcon />
      </IconButton>
    </Tooltip>
  );
};

const contactFabSx = {
  position: 'fixed',
  right: 16,
  bottom: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', md: 24 },
  zIndex: 1200,
  background: BRAND.accent,
  color: BRAND.onAccent,
  boxShadow: `0 8px 20px ${brandAlpha('primary', 0.18)}`,
  '&:hover': {
    background: BRAND.accentDark,
  },
};

export const ContactSupportFab = ({ sx }) => {
  const { t } = useTranslation();
  const { openChat, isAppAdmin, isStoreChat, isOpen } = useSupportChat();
  const label = t(contactLabelKey(isStoreChat));

  if (isAppAdmin || isOpen) {
    return null;
  }

  return (
    <Tooltip title={label}>
      <Fab
        color="primary"
        aria-label={label}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openChat();
        }}
        sx={{ ...contactFabSx, ...sx }}
      >
        <HeadsetMicIcon />
      </Fab>
    </Tooltip>
  );
};

export const ContactSupportButton = ({ onClick, sx, ...props }) => {
  const { t } = useTranslation();
  const { openChat, isAppAdmin, isStoreChat } = useSupportChat();

  if (isAppAdmin) {
    return null;
  }

  return (
    <Button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openChat();
        onClick?.(event);
      }}
      startIcon={<ContactSupportIcon />}
      sx={{
        justifyContent: 'flex-start',
        textTransform: 'none',
        color: BRAND.accentText,
        fontWeight: 600,
        ...sx,
      }}
      {...props}
    >
      {t(contactLabelKey(isStoreChat))}
    </Button>
  );
};

export default ContactSupportIconButton;
